import {ParamComponent} from 'panel';
import isEqual from 'lodash/isEqual';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  CellClickEvent,
  CellCoordinates,
  CollapseChangeEvent,
  SortChangeEvent,
} from 'common/components/data-table/types';
import {hasRetentionData} from 'common/report/retention/util/parsing';
import type {RowWithAggregate} from 'common/report/retention/util/parsing';
import {BookmarkController} from 'common/report/retention/bookmark-controller';
import {MultimetricBookmarkController} from 'common/report/retention/bookmark-controller/multimetric';
import {RetentionBookmarkType} from 'common/report/retention/charts/types';
import type {BookmarkData} from 'common/report/retention/charts/types';
import {SortConfig} from 'common/report/util/sorting/types';
import {TablePopoverState} from 'common/report/retention/visualization/table';
import {RetentionDataTableRow} from 'common/report/retention/util/types';

import {template} from './template';
import css from './index.inline.styl';

interface InsightsRetentionTableState {
  bookmarkController: BookmarkController;
  results: RowWithAggregate[];
  popoverState: TablePopoverState;
}

type InsightsRetentionTableParams = {
  rowData: RowWithAggregate[];
  bookmarkData: BookmarkData;
  projectId: number;
  projectTimezone: string;
  resizingLayout: boolean;
  onSortChange: (sorting: SortConfig) => void;
  onExpandedSegmentsChange: (expandedSegments: string[]) => void;
};

type InsightsRetentionTableJSX = {
  params?: InsightsRetentionTableParams;
};

export class InsightsRetentionTable extends ParamComponent<
  InsightsRetentionTableParams,
  InsightsRetentionTableState,
  ChartsContextRegistry
> {
  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        bookmarkController: null,
        results: null,
        popoverState: {
          open: false,
        },
      },
      params: {
        rowData: Array,
        bookmarkData: Object,
        projectId: Number,
        projectTimezone: String,
        resizingLayout: Boolean,
        onSortChange: Function,
        onExpandedSegmentsChange: Function,
      },
      hooks: {
        preUpdate: (_newState, newParams?: InsightsRetentionTableParams) => {
          if (newParams && !isEqual(newParams.bookmarkData, this.params.bookmarkData)) {
            this.handleBookmarkDataUpdate(newParams);
          }
          if (newParams && !isEqual(newParams.rowData, this.params.rowData)) {
            this.update({results: newParams.rowData});
          }
        },
      },
    };
  }

  connectedCallback() {
    this.update({
      results: this.params.rowData,
    });
    this.handleBookmarkDataUpdate(this.params);
    super.connectedCallback();
  }

  handleBookmarkDataUpdate(params: InsightsRetentionTableParams) {
    const {bookmark, queryResults, type} = params.bookmarkData;
    if (this.state.bookmarkController && this.state.bookmarkController.isEquivalent(bookmark, queryResults)) {
      return;
    }
    if (type === RetentionBookmarkType.MULTIMETRIC) {
      this.update({
        bookmarkController: new MultimetricBookmarkController(bookmark, queryResults),
      });
    }
  }

  hasResults() {
    return hasRetentionData(this.state.results);
  }

  handleSortChange = (ev: SortChangeEvent) => {
    const sorting = ev.detail.sorting;
    this.params?.onSortChange(sorting);
  };

  handleCollapseChange = (ev: CollapseChangeEvent) => {
    if (!this.state.bookmarkController) {
      return;
    }
    const {row, isCollapsed} = ev.detail;
    const expandedSegmentSet = new Set(this.state.bookmarkController?.getLegendExpandedSegments());
    if (isCollapsed) {
      expandedSegmentSet.delete(row.identifier);
    } else {
      expandedSegmentSet.add(row.identifier);
    }
    this.params?.onExpandedSegmentsChange(Array.from(expandedSegmentSet));
  };

  handlePopoverChange = (open: boolean) => {
    this.update({popoverState: {open}});
  };

  getSelectedCell(): null | CellCoordinates {
    const {popoverState} = this.state;
    if (popoverState.open) {
      return {
        columnIdx: popoverState.absoluteColumnIdx,
        rowIdx: popoverState.rowIdx,
        groupedRowIdx: popoverState.groupedRowIdx,
      };
    }
    return null;
  }

  handleCellClick = (ev: CellClickEvent) => {
    if (!this.state.bookmarkController) {
      return;
    }
    const {field, context, target, bgColor, textColor} = ev.detail;
    const row = ev.detail.row as RetentionDataTableRow;
    const columnIdx = parseInt(field);
    if (isNaN(columnIdx) || columnIdx < 0 || columnIdx >= row.cells.length) {
      return;
    }

    const cell = row.cells[columnIdx];
    const popover = this.state.bookmarkController.createPopover({
      projectTimezone: this.params.projectTimezone,
      colIdx: columnIdx,
      rowIdx: context.rowIdx,
      groupedRowIdx: context.groupedRowIdx,
      absoluteValue: cell.formatted_value,
      absoluteDropOffValue: cell.absolute_drop_off_value,
      cohortSize: cell.cohort_size,
      completeDate: cell.complete_date,
      enteredValue: cell.entered_value,
      enteredDate: cell.date,
      incomplete: cell.incomplete,
      isAggregated: !row.isGroupedRow,
    });
    this.update({
      popoverState: {
        retainedPercent: cell.formatted_percent,
        dropOffPercent: cell.percent_drop_off_value,
        bgColor,
        textColor,
        columnIdx,
        absoluteColumnIdx: context.columnIdx,
        rowIdx: context.rowIdx,
        groupedRowIdx: context.groupedRowIdx,
        popover,
        open: true,
        targetCell: target,
      },
    });
  };
}

export const InsightsRetentionTableEl = defineAndMakeJsxFactory<InsightsRetentionTableJSX>(
  `insights-retention-table`,
  InsightsRetentionTable,
);
export const InsightsRetentionTableReact = panel2react<InsightsRetentionTableJSX>(
  `insights-retention-table`,
  InsightsRetentionTable,
);
